<template>
    <div
        class="relative w-full h-full"
        ref="container"
    >
        <!-- Skeleton Loader -->
        <div
            v-if="loading"
            class="skeleton-loader"
            :style="{
                ...skeletonBaseStyle,
                ...imgStyle,
            }"
            :class="imgClass"
        ></div>

        <!-- Actual Image -->
        <img
            v-if="isVisible"
            :src="src"
            :alt="alt"
            @load="onLoad"
            @error="onError"
            class="image object-cover"
            :class="[imgClass, { hidden: loading, 'fade-in': !loading }]"
            :style="imgStyle"
            style="border-radius: 0.5rem;"
        />
    </div>
</template>

<script>
export default {
    name: "SkeletonImage",
    props: {
        src: {
            type: String,
            required: true,
        },
        alt: {
            type: String,
            default: "Image",
        },
        imgClass: {
            type: [String, Array, Object],
            default: "",
        },
        imgStyle: {
            type: String,
            default: "",
        },
        fallbackText: {
            type: String,
            default: "Image not available",
        },
    },
    data() {
        return {
            loading: true,
            error: false,
            isVisible: false,
            observer: null,
        };
    },
    computed: {
        skeletonBaseStyle() {
            return {
                width: "100%",
                height: "80px",
                borderRadius: "8px",
            };
        },
    },
    mounted() {
        this.initObserver();
    },
    beforeDestroy() {
        if (this.observer && this.$refs.container) {
            this.observer.unobserve(this.$refs.container);
        }
    },
    methods: {
        initObserver() {
            if (!("IntersectionObserver" in window)) {
                this.isVisible = true;
                return;
            }

            this.observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            this.isVisible = true;
                            this.observer.unobserve(this.$refs.container);
                        }
                    });
                },
                {
                    root: null,
                    rootMargin: "100px",
                    threshold: 0.1,
                }
            );

            if (this.$refs.container) {
                this.observer.observe(this.$refs.container);
            }
        },
        onLoad(event) {
            const img = event.target;
            if (img.complete && img.naturalHeight !== 0) {
                this.loading = false;
            }
        },
        onError() {
            this.loading = false;
            this.error = true;
        },
    },
};
</script>

<style scoped>
.relative {
    position: relative;
    display: inline-block;
    width: 100%;
}

.skeleton-loader {
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: shimmer 1.5s infinite;
}

.image {
    display: block;
    width: 100%;
    opacity: 0;
    transition: opacity 0.3s ease-in-out;
}

.hidden {
    display: none;
}

.fade-in {
    opacity: 1;
}

@keyframes shimmer {
    0% {
        background-position: -200% 0;
    }

    100% {
        background-position: 200% 0;
    }
}
</style>
